import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, Fab, IconButton, InputBase, Paper, Portal, Typography} from '@mui/material';
import {Close, HelpOutline, Send} from '@mui/icons-material';
import FelicityReply from './FelicityReply';
import FelicityVoicePanel, {avatarWasOpen} from './FelicityVoicePanel';
import {readShow, readWalk, replyWords, stripSentinel} from './replyBlocks';
import {startGuide, stopGuide, describeScreen, withNamedGroup} from './screenGuide';
import {FELICITY_CHAT_URL, FELICITY_NO_WORDS, FELICITY_NOT_CONNECTED, FELICITY_SHOWN, FELICITY_WELCOME} from '../../constants/felicity';
import useUser from '../../utility/hooks/useUser';
import {audiencesForRoles} from '../../help/audience';
import useFelicityPass from './useFelicityPass';
import {PHONE_ICON_ONLY_SX} from '../../AppLayout/topBarStyle';
import useDraggableOffset from './useDraggableOffset';
import {panelPlacement} from './panelPlacement';
import useOpenWindow from './useOpenWindow';
import useFelicitySetup from './useFelicitySetup';
import {readAnswerStream, shownSoFar} from './felicityStream';

const FAB_STYLE = {
    position: 'fixed',
    bottom: 24,
    right: 24,
    zIndex: 1300,
    textTransform: 'none',
    touchAction: 'none'
};

const PANEL_STYLE = {
    position: 'fixed',
    bottom: 96,
    right: 24,
    zIndex: 1300,
    width: 368,
    maxWidth: 'calc(100vw - 48px)',
    maxHeight: 512,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 3
};

export // AD-2653: sent by the screen when a walkthrough step has been done. It is never shown as if the person typed it.
const STEP_DONE = 'Done';
const SIDE_PANEL_WIDTH = 380;

// AD-2653: on a phone the panel covers the screen. During a walkthrough it shrinks to a strip along the
// bottom showing the current step, so the page and the part lit up stay in view. Tapping it opens the chat.
const PHONE_WIDTH = 900;
const onPhone = () => window.innerWidth < PHONE_WIDTH;

const STRIP_STYLE = {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1300,
    maxHeight: '35vh',
    overflowY: 'auto',
    borderRadius: '12px 12px 0 0',
    px: 2,
    py: 1.5,
    cursor: 'pointer'
};

const SIDE_PANEL_STYLE = {
    top: 0,
    bottom: 0,
    right: 0,
    // A phone shows the panel across the whole screen.
    width: {xs: '100vw', md: SIDE_PANEL_WIDTH},
    minWidth: 0,
    maxWidth: '100vw',
    maxHeight: 'none',
    height: '100vh',
    borderRadius: 0,
    transform: 'none'
};

const HEADER_STYLE = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 1.5,
    px: 2.5,
    py: 2,
    borderBottom: '1px solid',
    borderColor: 'divider'
};

const LIST_STYLE = {
    flex: 1,
    overflowY: 'auto',
    px: 2.5,
    py: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5
};

const FORM_STYLE = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    p: 1.5,
    borderTop: '1px solid',
    borderColor: 'divider'
};

const INPUT_STYLE = {
    flex: 1,
    height: 40,
    px: 2,
    borderRadius: 8,
    border: '1px solid',
    borderColor: 'divider',
    fontSize: 14
};

const bubbleStyle = (role) => ({
    px: 2,
    py: 1.25,
    borderRadius: 3,
    ...(role === 'user'
        ? {
            ml: 4,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            borderBottomRightRadius: 4
        }
        : {
            mr: 4,
            backgroundColor: 'action.hover',
            borderBottomLeftRadius: 4
        })
});

/**
 * Felicity inside Admicity. The request carries the conversation and the account type. Felicity's
 * service refuses a request that leaves the account type out, the same way it does for the help
 * centre panel. The account type comes from the roles on the signed-in account.
 *
 * The request also asks Felicity to name the parts of the screen her answer sends a person to.
 * Those are outlined one at a time as the person presses them.
 */
const FelicityPanel = ({inTopBar = false}) => {
    const {user} = useUser();
    const audience = useMemo(() => audiencesForRoles(user.roles)[0] ?? null, [user.roles]);
    const passFor = useFelicityPass();
    // AD-2653: a page loaded afresh during a conversation with the avatar shows her again, still talking.
    const setup = useFelicitySetup();
    const [open, setOpen] = useState(avatarWasOpen);
    const [mode, setMode] = useState(() => (avatarWasOpen() ? 'avatar' : 'text'));
    const [messages, setMessages] = useState([{role: 'assistant', content: FELICITY_WELCOME}]);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [walking, setWalking] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const windowOpen = useOpenWindow(open);
    const endOfList = useRef(null);
    const drag = useDraggableOffset(`${open}-${mode}`);
    // AD-2653: in the top bar Felicity opens as a side panel down the right of the screen, and the page
    // narrows to make room, so she never covers a menu or a part a walkthrough lights up.
    const panelPlace = inTopBar ? SIDE_PANEL_STYLE : panelPlacement(false, drag.style);
    const panelRef = drag.panelRef;

    useEffect(() => {
        if (!inTopBar) {
            return undefined;
        }

        document.documentElement.style.setProperty('--felicity-panel-width', open ? `${SIDE_PANEL_WIDTH}px` : '0px');
        document.body.classList.toggle('felicity-open', open);

        return () => {
            document.documentElement.style.setProperty('--felicity-panel-width', '0px');
            document.body.classList.remove('felicity-open');
        };
    }, [inTopBar, open]);

    // Felicity always opens on the typed chat, even when the last conversation was with the avatar.
    const toggle = () => {
        if (!open) {
            setMode('text');
        }
        setOpen(!open);
    };

    useEffect(() => stopGuide, []);

    useEffect(() => {
        if (open) {
            endOfList.current?.scrollIntoView({block: 'end'});
        }
    }, [messages, open, sending]);

    // The conversation so far, read by a walkthrough step that finishes after the panel has re-rendered.
    const latest = useRef(messages);
    latest.current = messages;

    const send = async (event) => {
        event.preventDefault();

        const question = draft.trim();

        if (!question || sending) {
            return;
        }

        await ask(question);
    };

    /**
     * Sends a question, or "Done" when a walkthrough step has been done on the screen (AD-2653). A step
     * of a walkthrough is outlined like any answer, and the panel watches for it to be done.
     */
    const ask = async (question) => {
        const next = [...latest.current, {role: 'user', content: question}];

        setMessages(next);
        setDraft('');
        setError(null);
        stopGuide();

        if (!FELICITY_CHAT_URL) {
            setMessages([...next, {role: 'assistant', content: FELICITY_NOT_CONNECTED}]);
            return;
        }

        setSending(true);

        try {
            const pass = await passFor();
            const response = await fetch(FELICITY_CHAT_URL, {
                method: 'POST',
                credentials: 'include',
                headers: pass
                    ? {'Content-Type': 'application/json', 'x-admicity-felicity-pass': pass}
                    : {'Content-Type': 'application/json'},
                body: JSON.stringify({messages: next, accountType: audience, highlight: true, screen: describeScreen(), setup, stream: true})
            });

            if (!response.ok) {
                throw new Error(String(response.status));
            }

            // The answer shows as it is written; the outline starts once it is complete.
            const reply = await readAnswerStream(response, (soFar) =>
                setMessages([...next, {role: 'assistant', content: stripSentinel(shownSoFar(soFar))}]));

            // AD-2660: a reply holding only the outline marks still says something, never an empty bubble.
            setMessages([...next, {role: 'assistant', content: replyWords(reply, FELICITY_SHOWN, FELICITY_NO_WORDS)}]);
            setWalking(Boolean(readWalk(reply)));
            setExpanded(false);
            startGuide(withNamedGroup(readShow(reply), stripSentinel(reply)), {walk: readWalk(reply), onDone: () => ask(STEP_DONE)});
        } catch {
            setError('That did not get through. Try again in a moment.');
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {inTopBar
? (
                <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    onClick={toggle}
                    aria-expanded={open}
                    aria-label={open ? 'Close Felicity' : 'Ask Felicity'}
                    data-felicity="button"
                    startIcon={open ? <Close/> : <HelpOutline/>}
                    sx={{textTransform: 'none', borderRadius: 5, mr: {xs: 1, sm: 2}, whiteSpace: 'nowrap', ...PHONE_ICON_ONLY_SX}}
                >
                    {/* Maria, 19 October 2026: just the icon on a phone */}
                    <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>{open ? 'Close Felicity' : 'Ask Felicity'}</Box>
                </Button>
            )
: (
                <Fab
                    color="primary"
                    variant={open ? 'circular' : 'extended'}
                    onClick={toggle}
                    aria-expanded={open}
                    aria-label={open ? 'Close Felicity' : 'Ask Felicity'}
                    ref={drag.handleRef}
                    data-felicity="button"
                    {...drag.handleProps}
                    sx={{...FAB_STYLE, ...drag.style}}
                >
                    {open ? <Close/> : <><HelpOutline sx={{mr: 1}}/>Ask Felicity</>}
                </Fab>
            )}

            <Portal container={windowOpen}>
            {open && mode === 'avatar' && (
                <FelicityVoicePanel
                    accountType={audience}
                    setup={setup}
                    onType={() => setMode('text')}
                    onClose={() => setOpen(false)}
                    panelRef={panelRef}
                    offset={panelPlace}
                />
            )}

            {open && mode === 'text' && walking && !expanded && onPhone() && (
                <Paper elevation={8} data-felicity="panel" sx={STRIP_STYLE} onClick={() => setExpanded(true)}>
                    {sending
                        ? <Typography variant="body2" color="text.secondary">Thinking…</Typography>
                        : <FelicityReply text={messages[messages.length - 1]?.content ?? ''}/>}
                    <Typography variant="caption" color="primary" sx={{display: 'block', mt: 1}}>
                        Tap to see the whole chat
                    </Typography>
                </Paper>
            )}

            {open && mode === 'text' && !(walking && !expanded && onPhone()) && (
                <Paper elevation={8} ref={panelRef} data-felicity="panel" sx={{...PANEL_STYLE, ...panelPlace}}>
                    <Box sx={HEADER_STYLE}>
                        <Box>
                            <Typography variant="subtitle2" fontWeight={600}>Felicity</Typography>
                        </Box>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setMode('avatar')}
                                sx={{flex: 'none', borderRadius: 8, textTransform: 'none'}}
                            >
                                Talk to Felicity
                            </Button>
                            <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close Felicity">
                                <Close fontSize="small"/>
                            </IconButton>
                        </Box>
                    </Box>

                    <Box sx={LIST_STYLE}>
                        {messages.map((message, index) => ({message, index})).filter(({message}) => message.content !== STEP_DONE || message.role !== 'user').map(({message, index}) => (
                            <Box key={index} sx={bubbleStyle(message.role)} data-role={message.role}>
                                {message.role === 'assistant'
                                    ? <FelicityReply text={message.content}/>
                                    : <Typography variant="body2">{message.content}</Typography>}
                            </Box>
                        ))}
                        {sending && messages[messages.length - 1]?.role !== 'assistant' && (
                            <Typography variant="body2" color="text.secondary" sx={{mr: 4, px: 2}}>
                                Thinking…
                            </Typography>
                        )}
                        {error && (
                            <Typography variant="body2" color="error" sx={{mr: 4, px: 2}}>
                                {error}
                            </Typography>
                        )}
                        <div ref={endOfList}/>
                    </Box>

                    <Box component="form" onSubmit={send} sx={FORM_STYLE}>
                        <InputBase
                            value={draft}
                            onChange={event => setDraft(event.target.value)}
                            placeholder="Ask a question"
                            inputProps={{'aria-label': 'Ask Felicity a question'}}
                            sx={INPUT_STYLE}
                        />
                        <IconButton
                            type="submit"
                            color="primary"
                            disabled={sending || draft.trim().length === 0}
                            aria-label="Send"
                        >
                            <Send fontSize="small"/>
                        </IconButton>
                    </Box>
                </Paper>
            )}
            </Portal>
        </>
    );
};

FelicityPanel.propTypes = {
    inTopBar: PropTypes.bool
};

export default FelicityPanel;
